import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DateRangePicker } from '@mui/x-date-pickers-pro';

import { useGetDynamicPricingSurchargeEstimates } from '~/hooks/apis/lere/useGetDynamicPricingSurchargeEstimates';
import { useGetEligibleSurchargeOffers } from '~/hooks/apis/lere/useGetEligibleSurchargeOffers';

import { Dayjs } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

import NameAndIdFormatter from './formatters/NameAndIdFormatter';
import { DynamicPricingSurchargeEstimateParams, LereTabComponentProps } from './types';

const DATE_FORMAT = 'YYYY-MM-DD';
const ELIGIBLE_OFFERS_PAGE_SIZE = 20;

type EligibleSurchargeOfferRow = {
  id: string;
  offerId: string;
};

const eligibleOfferColumns: Array<GridColDef<EligibleSurchargeOfferRow>> = [
  {
    field: 'offerId',
    headerName: 'Offer ID',
    display: 'flex',
    flex: 1,
    renderCell: ({ row }) => <NameAndIdFormatter id={row.offerId} to={`/offers/${row.offerId}`} />,
  },
];

function formatEstimate(amount: number): string {
  const formattedAmount = currencyFormatter('AUD', Math.abs(amount));
  return amount > 0 ? `+${formattedAmount}` : amount < 0 ? `-${formattedAmount}` : formattedAmount;
}

export default function SurchargeEstimates({ onLereError }: LereTabComponentProps) {
  const [offerId, setOfferId] = useState('');
  const [checkInDate, setCheckInDate] = useState<Dayjs | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Dayjs | null>(null);
  const [submittedParams, setSubmittedParams] = useState<DynamicPricingSurchargeEstimateParams>();
  const [requestVersion, setRequestVersion] = useState(0);

  const dateRangeError = useMemo(() => {
    if (checkOutDate?.isBefore(checkInDate, 'day')) {
      return 'Check-out date must be on or after the check-in date.';
    }

    return undefined;
  }, [checkInDate, checkOutDate]);

  const {
    data: estimates,
    error,
    isFetching,
  } = useGetDynamicPricingSurchargeEstimates(submittedParams, {
    requestVersion,
  });
  const {
    data: eligibleSurchargeOffers,
    error: eligibleSurchargeOffersError,
    isLoading: isLoadingEligibleSurchargeOffers,
  } = useGetEligibleSurchargeOffers();

  useEffect(() => {
    if (error) {
      onLereError(error);
    }
  }, [error, onLereError]);

  useEffect(() => {
    if (eligibleSurchargeOffersError) {
      onLereError(eligibleSurchargeOffersError);
    }
  }, [eligibleSurchargeOffersError, onLereError]);

  const canSubmit = Boolean(offerId.trim() && checkInDate && checkOutDate && !dateRangeError);
  const estimateRows = Object.entries(estimates ?? {}).sort(([firstRoom], [secondRoom]) =>
    firstRoom.localeCompare(secondRoom),
  );
  const eligibleOfferRows = useMemo(
    () => (eligibleSurchargeOffers?.offers ?? []).map((offerId) => ({ id: offerId, offerId })),
    [eligibleSurchargeOffers],
  );

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!canSubmit || !checkInDate || !checkOutDate) {
      return;
    }

    setSubmittedParams({
      offerId: offerId.trim(),
      checkInDate: checkInDate.format(DATE_FORMAT),
      checkOutDate: checkOutDate.format(DATE_FORMAT),
    });
    setRequestVersion((currentVersion) => currentVersion + 1);
  }

  return (
    <>
      <Paper component="form" elevation={0} onSubmit={handleSubmit} sx={{ border: 1, borderColor: 'divider', p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
          <TextField
            required
            label="Offer ID"
            value={offerId}
            onChange={(event) => setOfferId(event.target.value)}
            sx={{ minWidth: { md: 300 } }}
          />
          <DateRangePicker
            calendars={1}
            value={[checkInDate, checkOutDate]}
            onChange={([newCheckInDate, newCheckOutDate]) => {
              setCheckInDate(newCheckInDate);
              setCheckOutDate(newCheckOutDate);
            }}
            slotProps={{
              textField: {
                required: true,
                error: !!dateRangeError,
                helperText: dateRangeError,
              },
            }}
          />
          <Button type="submit" variant="contained" disabled={!canSubmit || isFetching} sx={{ mt: { md: 1 } }}>
            {isFetching ? 'Estimating…' : 'Estimate surcharge'}
          </Button>
        </Stack>
      </Paper>

      {submittedParams && !isFetching && !error && estimateRows.length === 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No surcharge estimates are available for this offer and date range.
        </Alert>
      )}

      {estimateRows.length > 0 && (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mt: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h2">Estimated surcharge by room type</Typography>
            <Typography color="text.secondary" variant="body2">
              Positive values increase the price; negative values reduce it. Amounts are in AUD.
            </Typography>
          </Box>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ bgcolor: 'action.hover' }}>
              <Box component="tr">
                <Box component="th" sx={{ p: 2, textAlign: 'left' }}>
                  Room type
                </Box>
                <Box component="th" sx={{ p: 2, textAlign: 'left' }}>
                  Experiment group A
                </Box>
                <Box component="th" sx={{ p: 2, textAlign: 'left' }}>
                  Experiment group B
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {estimateRows.map(([roomType, experimentGroups]) => (
                <Box
                  component="tr"
                  key={roomType}
                  sx={{ '&:not(:last-child) td': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <Box component="td" sx={{ p: 2 }}>
                    <Typography fontWeight={600}>{roomType}</Typography>
                  </Box>
                  <Box component="td" sx={{ p: 2 }}>
                    <Chip
                      color={experimentGroups.A > 0 ? 'success' : experimentGroups.A < 0 ? 'error' : 'default'}
                      label={formatEstimate(experimentGroups.A)}
                      variant={experimentGroups.A === 0 ? 'outlined' : 'filled'}
                    />
                  </Box>
                  <Box component="td" sx={{ p: 2 }}>
                    <Chip
                      color={experimentGroups.B > 0 ? 'success' : experimentGroups.B < 0 ? 'error' : 'default'}
                      label={formatEstimate(experimentGroups.B)}
                      variant={experimentGroups.B === 0 ? 'outlined' : 'filled'}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mt: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h2">Offers eligible for surcharge</Typography>
          <Typography color="text.secondary" variant="body2">
            Offers with at least one date currently eligible for a surcharge.
          </Typography>
        </Box>
        <DataGrid
          autoHeight
          columns={eligibleOfferColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          loading={isLoadingEligibleSurchargeOffers}
          pageSizeOptions={[10, ELIGIBLE_OFFERS_PAGE_SIZE, 50]}
          rows={eligibleOfferRows}
          initialState={{ pagination: { paginationModel: { pageSize: ELIGIBLE_OFFERS_PAGE_SIZE, page: 0 } } }}
        />
      </Paper>
    </>
  );
}
